import { Injectable, NgZone } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";

// TypeScript interface for webkit Speech Recognition
interface IWindow extends Window {
  webkitSpeechRecognition: any;
  SpeechRecognition: any;
}

@Injectable({
  providedIn: "root",
})
export class SpeechService {
  private recognition: any;
  private transcriptionSubject = new BehaviorSubject<string>("");
  private isRecordingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new Subject<string>();
  private finalTranscript = ""; // Accumulate final results
  private selectedLanguage = "en-US"; // Default language
  private hasReceivedSpeech = false; // Track if any speech was detected

  // Silence-based auto-stop (industry-standard dictation UX): recording ends
  // automatically after a short pause so the user doesn't have to tap stop.
  private silenceTimer: any = null;
  private silenceTimeoutMs = 3000; // Pause length (ms) that triggers auto-stop

  // Medical terminology autocorrect dictionary - EXPANDED
  private medicalCorrections: { [key: string]: string } = {
    // Common misrecognitions
    patient: "patient",
    perscription: "prescription",
    pescription: "prescription",
    "per scription": "prescription",

    // Diabetes-related
    diabeetus: "diabetes",
    diabetic: "diabetic",
    diabeties: "diabetes",
    "die a betes": "diabetes",
    "die betes": "diabetes",
    "type 2 diabetes": "type 2 diabetes",
    "type to diabetes": "type 2 diabetes",
    "type too diabetes": "type 2 diabetes",
    glucose: "glucose",
    insulin: "insulin",
    "in solin": "insulin",

    // Cardiovascular
    "high per tension": "hypertension",
    hypertension: "hypertension",
    "blood pressure": "blood pressure",
    "heart attack": "heart attack",
    cardiac: "cardiac",
    "car diac": "cardiac",
    cholesterol: "cholesterol",
    "coleesterol": "cholesterol",
    arrhythmia: "arrhythmia",
    "a rhythm mia": "arrhythmia",

    // Medications
    medication: "medication",
    medications: "medications",
    "anti biotic": "antibiotic",
    "anti biotics": "antibiotics",
    antibiotic: "antibiotic",
    antibiotics: "antibiotics",
    "stat in": "statin",
    statin: "statin",
    statins: "statins",
    "met formin": "metformin",
    metformin: "metformin",
    "lip it or": "lipitor",
    lipitor: "lipitor",


    // Pharmacy & Prescriptions
    pharmacy: "pharmacy",
    "farm a see": "pharmacy",
    pharmacist: "pharmacist",
    "farm a cist": "pharmacist",
    prescription: "prescription",
    prescriptions: "prescriptions",
    prescriber: "prescriber",
    prescribers: "prescribers",
    "specialty pharmacy": "specialty pharmacy",
    "special tea pharmacy": "specialty pharmacy",
    
    // Specialty Pharmacy Providers (multi-word mispronunciations only; single
    // common words like "amber"/"prime"/"noble" were removed to avoid rewriting
    // ordinary speech). Add specific terms at runtime via addMedicalTerm().
    "cvs specialty": "CVS Specialty",
    "ak redo": "Accredo",
    "wall greens": "Walgreens",
    "sen derra": "Senderra",
    
    "patient assistance program": "patient assistance program",



    // Treatment & Therapy
    treatment: "treatment",
    therapy: "therapy",
    therapies: "therapies",
    "time to therapy": "time to therapy",
    clinical: "clinical",
    "clinical trial": "clinical trial",
    "clin ical": "clinical",
    chemotherapy: "chemotherapy",
    "kemo therapy": "chemotherapy",
    "chemo therapy": "chemotherapy",

    // Medical Procedures
    surgery: "surgery",
    surgical: "surgical",
    diagnosis: "diagnosis",
    diagnoses: "diagnoses",
    "die ag no sis": "diagnosis",
    procedure: "procedure",
    "pro seed your": "procedure",
    biopsy: "biopsy",
    "buy op see": "biopsy",

    // Healthcare Facilities
    hospital: "hospital",
    clinic: "clinic",
    emergency: "emergency",
    "emergency room": "emergency room",
    "i c u": "ICU",
    "intensive care": "intensive care",

    // Patient Journey & Status
    admission: "admission",
    readmission: "readmission",
    "re admission": "readmission",
    discharge: "discharge",
    shipment: "shipment",
    shipments: "shipments",
    "ship ment": "shipment",
    "ship meant": "shipment",
    enrollment: "enrollment",
    "in roll ment": "enrollment",
    "patient status": "patient status",
    "patient stat us": "patient status",
    status: "status",
    "stat us": "status",
    "status date": "status date",
    "status code": "status code",
    
    // Pharmacy Operations
    dispense: "dispense",
    dispensed: "dispensed",
    "dis pense": "dispense",
    "dis pensed": "dispensed",
    inventory: "inventory",
    "in ven tory": "inventory",
    quantity: "quantity",
    "quan tity": "quantity",
    "quant ity": "quantity",

    // Common Medical Terms
    symptom: "symptom",
    symptoms: "symptoms",
    "simp tom": "symptom",
    dosage: "dosage",
    dose: "dose",
    refill: "refill",
    "re fill": "refill",
    "side effect": "side effect",
    "side effects": "side effects",
    allergic: "allergic",
    allergy: "allergy",
    allergies: "allergies",
    "al er gic": "allergic",
    chronic: "chronic",
    "cron ic": "chronic",
    acute: "acute",

    // Data/Analytics Terms (from SCRAII context)
    "patient journey": "patient journey",
    "patient list": "patient list",
    analytics: "analytics",
    "anna lit ics": "analytics",
    "data quality": "data quality",
    dashboard: "dashboard",
    "dash board": "dashboard",
    metrics: "metrics",
    "met rics": "metrics",
    kpi: "KPI",
    "k p i": "KPI",
    "key performance indicator": "KPI",
    
    // Medical Specialties
    cardiology: "cardiology",
    "card iology": "cardiology",
    oncology: "oncology",
    "on col ogy": "oncology",
    neurology: "neurology",
    "new rology": "neurology",
    radiology: "radiology",
    "ray d ology": "radiology",
    
    // Common Lab Tests
    "blood test": "blood test",
    "lab work": "lab work",
    "x-ray": "x-ray",
    "ex ray": "x-ray",
    "m r i": "MRI",
    "c t scan": "CT scan",
    "cat scan": "CT scan",
  };

  // Public observables
  public transcription$: Observable<string> =
    this.transcriptionSubject.asObservable();
  public isRecording$: Observable<boolean> =
    this.isRecordingSubject.asObservable();
  public error$: Observable<string> = this.errorSubject.asObservable();

  constructor(private ngZone: NgZone) {
    this.initializeRecognition();
  }

  // Initialize the speech recognition instance
  private initializeRecognition(): void {
    if (!this.isSupported()) {
      console.warn("Speech recognition is not supported in this browser");
      return;
    }

    const { webkitSpeechRecognition } = window as any as IWindow;
    this.recognition = new webkitSpeechRecognition();

    // Configure recognition settings
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = this.selectedLanguage;
    // Use a single alternative: the engine's top result is its most confident
    // transcription. Re-ranking alternatives client-side hurt accuracy.
    this.recognition.maxAlternatives = 1;

    // Set up event handlers
    this.setupEventHandlers();
  }

  // Set up all event handlers for speech recognition
  private setupEventHandlers(): void {
    if (!this.recognition) {
      return;
    }

    // Handle speech recognition results with optimized real-time display
    this.recognition.onresult = (event: any) => {
      this.ngZone.run(() => {
        this.hasReceivedSpeech = true; // Mark that we received speech
        let interimTranscript = "";

        // Process all results
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];

          if (result.isFinal) {
            // Use the engine's top transcription (most confident) and apply a
            // light, safe autocorrect for common misrecognitions only.
            this.finalTranscript += this.correctMedicalTerms(result[0].transcript) + " ";
          } else {
            // For interim results, use first alternative for real-time display
            interimTranscript += result[0].transcript;
          }
        }

        // Any speech activity resets the silence countdown
        this.resetSilenceTimer();

        // Emit combined transcript (final + interim for real-time display)
        const currentTranscript = this.finalTranscript + interimTranscript;
        this.transcriptionSubject.next(currentTranscript);
      });
    };

    // Handle recognition start
    this.recognition.onstart = () => {
      this.ngZone.run(() => {
        this.isRecordingSubject.next(true);
        // Do NOT arm the silence timer here: there is latency before the first
        // result and the user may pause before speaking. The timer is armed
        // only once real speech arrives (see onresult), so we never cut off
        // before the user has said anything.
        console.log("Speech recognition started");
      });
    };

    // Handle recognition end
    this.recognition.onend = () => {
      this.ngZone.run(() => {
        this.clearSilenceTimer();
        this.isRecordingSubject.next(false);
        console.log("Speech recognition ended");
      });
    };

    // Handle errors
    this.recognition.onerror = (event: any) => {
      this.ngZone.run(() => {
        this.clearSilenceTimer();
        let errorMessage = "Speech recognition error occurred";
        let shouldShowError = true;

        switch (event.error) {
          case "no-speech":
            // Don't show error if user stopped immediately without speaking
            if (!this.hasReceivedSpeech) {
              shouldShowError = false;
              console.log("Recording stopped before speech detected");
            } else {
              errorMessage = "No speech was detected. Please try again.";
            }
            break;
          case "audio-capture":
            errorMessage = "No microphone was found. Ensure it is connected.";
            break;
          case "not-allowed":
            errorMessage =
              "Microphone permission was denied. Please allow access.";
            break;
          case "network":
            errorMessage =
              "Network error occurred. Please check your connection.";
            break;
          case "aborted":
            // Don't show error for manual abort
            shouldShowError = false;
            console.log("Speech recognition was aborted");
            break;
          default:
            errorMessage = `Speech recognition error: ${event.error}`;
        }

        if (shouldShowError) {
          console.error("Speech recognition error:", event.error);
          this.errorSubject.next(errorMessage);
        }
        this.isRecordingSubject.next(false);
      });
    };
  }

  // Check if speech recognition is supported in the current browser
  public isSupported(): boolean {
    const windowWithSpeech = window as any as IWindow;
    return !!(
      windowWithSpeech.webkitSpeechRecognition ||
      windowWithSpeech.SpeechRecognition
    );
  }

  // Start speech recognition
  public startRecording(): void {
    if (!this.isSupported()) {
      this.errorSubject.next(
        "Speech recognition is not supported in this browser"
      );
      return;
    }

    if (!this.recognition) {
      this.initializeRecognition();
    }

    try {
      // Reset accumulated transcript for new recording session
      this.finalTranscript = "";
      this.hasReceivedSpeech = false; // Reset speech detection flag
      this.transcriptionSubject.next("");

      // Request enhanced audio with noise suppression
      this.requestEnhancedAudio()
        .then(() => {
          this.recognition.start();
        })
        .catch(() => {
          // Fallback to regular start if enhanced audio fails
          this.recognition.start();
        });
    } catch (error) {
      console.error("Error starting speech recognition:", error);
      this.errorSubject.next("Failed to start speech recognition");
    }
  }

  // Stop speech recognition
  public stopRecording(): void {
    this.clearSilenceTimer();
    if (this.recognition && this.isRecordingSubject.value) {
      try {
        this.recognition.stop();
      } catch (error) {
        console.error("Error stopping speech recognition:", error);
      }
    }
  }

  // Restart the silence countdown. Called on each speech result so recording
  // auto-stops only after a genuine pause that follows actual speech.
  private resetSilenceTimer(): void {
    this.clearSilenceTimer();
    this.silenceTimer = setTimeout(() => {
      this.ngZone.run(() => this.stopRecording());
    }, this.silenceTimeoutMs);
  }

  // Cancel any pending auto-stop timer.
  private clearSilenceTimer(): void {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
  }

  // Configure the auto-stop silence threshold in milliseconds.
  public setSilenceTimeout(ms: number): void {
    this.silenceTimeoutMs = Math.max(500, ms);
  }

  // Get current recording status
  public get isRecording(): boolean {
    return this.isRecordingSubject.value;
  }

  // Set the language for speech recognition
  public setLanguage(languageCode: string): void {
    this.selectedLanguage = languageCode;
    if (this.recognition) {
      this.recognition.lang = languageCode;
    }
  }

  // Get current language
  public getLanguage(): string {
    return this.selectedLanguage;
  }

  // Get list of supported languages
  public getSupportedLanguages(): { code: string; name: string }[] {
    return [
      { code: "en-US", name: "English (US)" },
      { code: "en-GB", name: "English (UK)" },
      { code: "en-IN", name: "English (India)" },
    ];
  }

  // Autocorrect common medical term misrecognitions
  private correctMedicalTerms(transcript: string): string {
    let corrected = transcript;

    // Apply corrections (case-insensitive)
    Object.keys(this.medicalCorrections).forEach((wrong) => {
      const regex = new RegExp(`\\b${wrong}\\b`, "gi");
      corrected = corrected.replace(regex, this.medicalCorrections[wrong]);
    });

    return corrected;
  }

  // Request enhanced audio with noise suppression
  private async requestEnhancedAudio(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      // Stop the stream immediately - we just needed to set the constraints
      stream.getTracks().forEach((track) => track.stop());
    } catch (error) {
      console.warn("Enhanced audio not available, using default:", error);
      throw error;
    }
  }

  // Add custom medical vocabulary to corrections
  public addMedicalTerm(incorrect: string, correct: string): void {
    this.medicalCorrections[incorrect.toLowerCase()] = correct;
  }
}
